/**
 * AI Scribe floating panel - minimizable, non-blocking.
 * Side “live” panel only appears in Dictate mode.
 */
import { createPortal } from "react-dom";
import {
  AlertTriangle,
  Check,
  FileText,
  GripVertical,
  History,
  Loader2,
  Mic,
  Minimize2,
  Pause,
  Play,
  Receipt,
  RotateCcw,
  Sparkles,
  Square,
  Users,
  X,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState, type PointerEvent } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { listScribeSessions, type ScribeSessionRecord } from "@/api/ai.api";
import { useAiScribeOptional } from "@/providers/ai-scribe-provider";
import { useAuthSafe } from "@/hooks/use-auth";
import { VoicePoweredOrb } from "@/components/ui/voice-powered-orb";
import { VoiceDictateButton } from "@/components/encounters/voice-dictate-button";

function formatElapsed(seconds: number) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

const PROCESS_STEPS = [
  { label: "Finishing capture", detail: "Saving what was recorded" },
  { label: "Writing your note", detail: "Turning the visit into a clinical draft" },
  { label: "Safety checks", detail: "Scanning for alerts and gaps" },
  { label: "Opening review", detail: "Almost ready - you'll edit before applying" },
] as const;

function processProgress(step: number): number {
  // Map step 0–3 to 15–100% so the bar always moves visibly.
  const pct = [15, 45, 75, 95];
  return pct[Math.min(step, pct.length - 1)] ?? 15;
}

export function AIScribeFloater() {
  const scribe = useAiScribeOptional();
  const currentUserId = useAuthSafe().user?.id;
  const [pastSessions, setPastSessions] = useState<ScribeSessionRecord[]>([]);
  /** Newest visit that was captured but never drafted (closed tab, crash, failed draft). */
  const [unfinishedSession, setUnfinishedSession] = useState<ScribeSessionRecord | null>(null);
  const [showSpeakers, setShowSpeakers] = useState(false);
  const [dictateError, setDictateError] = useState("");

  // Draft recovery: list previous scribe sessions for this encounter.
  const scribePhase = scribe?.phase;
  const scribeEncounterId = scribe?.meta?.encounterId;
  useEffect(() => {
    if (scribePhase === "ready" && scribeEncounterId) {
      listScribeSessions(scribeEncounterId)
        .then((sessions) => {
          const drafted = sessions.filter(
            (s) => (s.status === "ready_for_review" || s.status === "applied") && s.sections_json,
          );
          setPastSessions(drafted);
          // Sessions are newest first; only offer an unfinished visit newer than any draft.
          const newestDraftAt = drafted[0] ? Date.parse(drafted[0].created_at) : 0;
          setUnfinishedSession(
            sessions.find(
              (s) =>
                // Only the clinician who recorded it can draft onto it.
                s.provider_id === currentUserId &&
                (s.status === "recording" || s.status === "failed") &&
                !s.sections_json &&
                Boolean(s.transcript?.trim()) &&
                Date.parse(s.created_at) > newestDraftAt,
            ) ?? null,
          );
        })
        .catch(() => {
          setPastSessions([]);
          setUnfinishedSession(null);
        });
    }
  }, [scribePhase, scribeEncounterId, currentUserId]);
  /** null = centered; otherwise top-left of panel in viewport coords */
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const dragRef = useRef<{
    startX: number;
    startY: number;
    origX: number;
    origY: number;
    moved: boolean;
  } | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  const clampPos = useCallback((x: number, y: number) => {
    const el = panelRef.current;
    const w = el?.offsetWidth ?? 420;
    const h = el?.offsetHeight ?? 320;
    const maxX = Math.max(8, window.innerWidth - w - 8);
    const maxY = Math.max(8, window.innerHeight - h - 8);
    return {
      x: Math.min(maxX, Math.max(8, x)),
      y: Math.min(maxY, Math.max(8, y)),
    };
  }, []);

  const onDragPointerDown = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      if ((e.target as HTMLElement).closest("button, a, input, textarea")) return;
      e.preventDefault();
      const el = panelRef.current;
      const rect = el?.getBoundingClientRect();
      const startLeft = pos?.x ?? rect?.left ?? window.innerWidth / 2 - 210;
      const startTop = pos?.y ?? rect?.top ?? window.innerHeight / 2 - 160;
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        origX: startLeft,
        origY: startTop,
        moved: false,
      };
      e.currentTarget.setPointerCapture(e.pointerId);
    },
    [pos],
  );

  const onDragPointerMove = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      const d = dragRef.current;
      if (!d) return;
      const dx = e.clientX - d.startX;
      const dy = e.clientY - d.startY;
      if (Math.abs(dx) + Math.abs(dy) > 3) d.moved = true;
      setPos(clampPos(d.origX + dx, d.origY + dy));
    },
    [clampPos],
  );

  const onDragPointerUp = useCallback((e: PointerEvent<HTMLDivElement>) => {
    if (dragRef.current) {
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
    }
    dragRef.current = null;
  }, []);

  // Reset to center when a new session starts
  useEffect(() => {
    if (scribe?.active && scribePhase !== "preview") setPos(null);
  }, [scribe?.active, scribe?.meta?.encounterId, scribePhase]);

  if (!scribe?.active || typeof document === "undefined") return null;

  const {
    view,
    phase,
    countdown,
    elapsed,
    processStep,
    transcript,
    sections,
    dictateText,
    meta,
    uncertainties,
    cdsAlerts,
    consentGiven,
    micPermission,
    speechReady,
    sttMode,
    speechStatus,
    voiceDetected,
    connectionState,
    audioLevel,
    safetyAlerts,
    billing,
    orders,
    insightsLoading,
    avsSummary,
    avsLoading,
    labeledTranscript,
    labelLoading,
    errorMessage,
    setConsentGiven,
    minimize,
    expand,
    beginCountdown,
    pause,
    resume,
    cancelRestart,
    finishProcess,
    openDictate,
    setDictateText,
    generateFromDictate,
    applyToNote,
    endSession,
    listenAgain,
    updateSection,
    generateAvsSummary,
    labelSpeakers,
    restoreSession,
  } = scribe;

  const safetyBanner = safetyAlerts.length > 0 && (
    <div className="w-full rounded-xl border-2 border-rose-400 bg-rose-50 p-3 text-left dark:bg-rose-500/10">
      <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-rose-800 dark:text-rose-300">
        <AlertTriangle className="h-4 w-4 shrink-0" />
        Safety concern detected
      </p>
      <ul className="mt-1.5 space-y-1.5 text-xs leading-relaxed text-rose-900 dark:text-rose-200">
        {safetyAlerts.map((alert) => (
          <li key={alert.category}>{alert.message}</li>
        ))}
      </ul>
    </div>
  );

  const statusLabel =
    phase === "listening"
      ? "Listening"
      : phase === "paused"
        ? "Paused"
        : phase === "countdown"
          ? "Starting"
          : phase === "processing"
            ? "Processing"
            : phase === "preview"
              ? "Draft ready"
              : phase === "dictate"
                ? "Dictating"
                : "Waiting to start";

  const pillTone =
    phase === "listening"
      ? "bg-rose-600 text-white"
      : phase === "paused"
        ? "bg-amber-500 text-amber-950"
        : phase === "processing" || phase === "countdown"
          ? "bg-primary text-primary-foreground"
          : phase === "preview"
            ? "bg-emerald-700 text-white"
            : "bg-slate-900 text-white";

  const showTimer =
    phase === "listening" || phase === "paused" || phase === "processing";
  const showDictateSide = phase === "dictate";
  const isReviewMode = phase === "preview";
  const widePanel = phase === "dictate";

  const panel = view === "minimized" ? (
    <button
      type="button"
      onClick={expand}
      className={cn(
        "fixed bottom-[11.5rem] right-5 z-[200] flex max-w-[min(100vw-2.5rem,22rem)] items-center gap-3 rounded-full px-4 py-3 shadow-xl transition hover:scale-[1.02]",
        pillTone,
      )}
      title={`AI Scribe · ${statusLabel}`}
    >
      <span
        className={cn(
          "flex h-9 w-9 items-center justify-center rounded-full bg-white/20",
          phase === "listening" && "animate-pulse",
        )}
      >
        {phase === "processing" || phase === "countdown" ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : phase === "paused" ? (
          <Pause className="h-4 w-4" />
        ) : phase === "preview" ? (
          <Check className="h-4 w-4" />
        ) : (
          <Mic className="h-4 w-4" />
        )}
      </span>
      <span className="text-left leading-tight">
        <span className="block text-xs font-semibold opacity-90">
          AI Scribe · {statusLabel}
        </span>
        <span className="block text-sm font-bold tabular-nums">
          {showTimer ? formatElapsed(elapsed) : meta?.patientName || "Session"}
        </span>
      </span>
    </button>
  ) : (
    <>
      {isReviewMode && <div className="ai-scribe-backdrop" aria-hidden />}
    <div
      ref={panelRef}
      className={cn(
        "ai-scribe-shell fixed z-[200]",
        isReviewMode
          ? "ai-scribe-shell--review"
          : widePanel
            ? "w-[min(920px,calc(100vw-2rem))]"
            : "w-[min(420px,calc(100vw-2rem))]",
        !isReviewMode && pos == null && "left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2",
      )}
      style={!isReviewMode && pos != null ? { left: pos.x, top: pos.y } : undefined}
      data-phase={phase}
    >
      <div className="ai-scribe-bloom" aria-hidden />
      <div
        className={cn(
          "ai-scribe-frame",
          isReviewMode ? "h-full" : "max-h-[min(560px,calc(100vh-3rem))]",
        )}
      >
        {!isReviewMode && (
          <>
            <div className="ai-scribe-orbit" aria-hidden />
            <div className="ai-scribe-orbit ai-scribe-orbit-slow" aria-hidden />
          </>
        )}
        <div
          className={cn(
            "ai-scribe-panel overflow-hidden",
            isReviewMode ? "flex h-full flex-col" : "max-h-[min(560px,calc(100vh-3rem))]",
          )}
        >
      <div
        className={cn(
          "ai-scribe-header relative z-[1] flex items-start justify-between gap-3 border-b border-border/60 px-4 py-3.5 select-none touch-none",
          !isReviewMode && "cursor-grab active:cursor-grabbing",
        )}
        onPointerDown={isReviewMode ? undefined : onDragPointerDown}
        onPointerMove={isReviewMode ? undefined : onDragPointerMove}
        onPointerUp={isReviewMode ? undefined : onDragPointerUp}
        onPointerCancel={isReviewMode ? undefined : onDragPointerUp}
        title={isReviewMode ? undefined : "Drag to move"}
      >
        <div className="flex min-w-0 items-start gap-1.5">
          {!isReviewMode && (
            <GripVertical className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground/70" aria-hidden />
          )}
          <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="ai-scribe-icon-badge relative flex h-6 w-6 items-center justify-center rounded-lg bg-primary/10 ring-1 ring-primary/20">
              <Sparkles className="h-3.5 w-3.5 shrink-0 text-primary" />
            </span>
            <h2 className="text-sm font-semibold tracking-tight text-foreground">
              {isReviewMode ? "Review draft note" : "AI Scribe"}
            </h2>
            {(phase === "listening" || phase === "paused") && (
              <Badge
                className={cn(
                  "text-[10px] font-bold shadow-sm",
                  phase === "listening"
                    ? "bg-rose-100 text-rose-800 ring-1 ring-rose-200/80 dark:bg-rose-500/15 dark:text-rose-300"
                    : "bg-amber-100 text-amber-900 ring-1 ring-amber-200/80 dark:bg-amber-500/15 dark:text-amber-200",
                )}
              >
                <span
                  className={cn(
                    "mr-1.5 inline-block h-1.5 w-1.5 rounded-full",
                    phase === "listening" ? "bg-rose-500 animate-pulse" : "bg-amber-500",
                  )}
                />
                {statusLabel} · {formatElapsed(elapsed)}
              </Badge>
            )}
          </div>
          <p className="mt-0.5 truncate text-xs text-muted-foreground">
            {isReviewMode
              ? "Edit sections below, then apply to the chart when ready"
              : meta?.patientName
                ? `Visit with ${meta.patientName}`
                : "Ambient capture for this visit"}
          </p>
          </div>
        </div>
        <div className="relative z-[1] flex shrink-0 items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            title="Minimize"
            onClick={minimize}
          >
            <Minimize2 className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground hover:text-destructive"
            title="End session"
            onClick={endSession}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div
        className={cn(
          "relative z-[1] flex-1 overflow-hidden",
          isReviewMode && "flex flex-col",
          showDictateSide && "grid grid-cols-1 md:grid-cols-2",
        )}
      >
        <div
          className={cn(
            "flex flex-col overflow-y-auto p-5",
            isReviewMode && "flex-1",
            showDictateSide && "border-b border-border md:border-b-0 md:border-r",
          )}
        >
          {phase === "ready" && (
            <div className="flex flex-col items-center justify-center gap-5 py-2 text-center">
              <div className="relative h-36 w-36 shrink-0">
                <VoicePoweredOrb
                  className="h-full w-full"
                  hue={210}
                  enableVoiceControl={false}
                />
              </div>
              <div className="max-w-sm space-y-1.5">
                <p className="text-sm font-bold tracking-tight">Ready to listen</p>
                <p className="text-xs leading-relaxed text-muted-foreground">
                  Short countdown, then the visit is transcribed live. Press Finish when done.
                </p>
                {micPermission === "denied" && (
                  <p className="text-xs font-medium text-destructive">
                    Microphone blocked. Allow mic access in browser settings or use Dictate mode.
                  </p>
                )}
                {micPermission === "unsupported" && (
                  <p className="text-xs font-medium text-destructive">
                    This browser does not support microphone capture. Use Dictate mode.
                  </p>
                )}
              </div>
              <label className="ai-scribe-consent flex max-w-sm items-start gap-2 rounded-lg border border-border bg-muted/30 p-3 text-left text-xs">
                <Checkbox
                  checked={consentGiven}
                  onCheckedChange={(v) => setConsentGiven(v === true)}
                  className="mt-0.5 border-white/30 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                />
                <span>
                  Patient has been notified that AI-assisted documentation may be used during this visit.
                </span>
              </label>
              {errorMessage && (
                <p className="max-w-sm text-xs font-medium text-destructive">{errorMessage}</p>
              )}
              <div className="flex flex-wrap justify-center gap-2">
                <Button
                  className="h-10 gap-2 px-5 shadow-md shadow-primary/20"
                  onClick={beginCountdown}
                  disabled={!consentGiven}
                >
                  <Mic className="h-4 w-4" />
                  Start listening
                </Button>
                <Button variant="outline" className="ai-scribe-secondary-btn h-10 gap-2 bg-background/60 backdrop-blur-sm" onClick={openDictate}>
                  Dictate instead
                </Button>
              </div>
              {unfinishedSession && (
                <div className="w-full max-w-sm rounded-xl border border-amber-300 bg-amber-50 p-3 text-left dark:border-amber-700 dark:bg-amber-950/40">
                  <p className="flex items-center gap-1.5 text-xs font-semibold text-amber-900 dark:text-amber-200">
                    <History className="h-3.5 w-3.5" />
                    Unfinished visit recording
                  </p>
                  <p className="mt-1 text-xs text-amber-800 dark:text-amber-300">
                    A visit from{" "}
                    {new Date(unfinishedSession.created_at).toLocaleString([], {
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}{" "}
                    was captured but no note was drafted. Recover the transcript to generate it.
                  </p>
                  <Button
                    size="sm"
                    className="mt-2 h-7 px-3 text-[11px]"
                    onClick={() => restoreSession(unfinishedSession)}
                  >
                    Recover transcript
                  </Button>
                </div>
              )}
              {pastSessions.length > 0 && (
                <div className="ai-scribe-past-drafts w-full max-w-sm rounded-xl border border-border bg-muted/20 p-3 text-left">
                  <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
                    <History className="h-3.5 w-3.5" />
                    Previous drafts for this encounter
                  </p>
                  <ul className="space-y-1.5">
                    {pastSessions.slice(0, 3).map((session) => (
                      <li key={session.id} className="flex items-center justify-between gap-2 text-xs">
                        <span className="min-w-0 truncate text-muted-foreground">
                          {new Date(session.created_at).toLocaleString([], {
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                          {" · "}
                          {session.template_type.toUpperCase()}
                          {session.status === "applied" && " · applied"}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 shrink-0 px-2 text-[11px]"
                          onClick={() => restoreSession(session)}
                        >
                          Restore
                        </Button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {phase === "countdown" && (
            <div className="flex flex-col items-center justify-center gap-3 py-8">
              <p className="text-xs font-semibold text-muted-foreground">
                Starting in
              </p>
              <div className="relative flex h-28 w-28 items-center justify-center">
                <span className="absolute inset-0 rounded-full border-2 border-primary/40 animate-ping" />
                <p className="text-7xl font-semibold tabular-nums text-primary drop-shadow-sm">
                  {countdown || 1}
                </p>
              </div>
              <Button variant="ghost" size="sm" className="gap-1.5" onClick={cancelRestart}>
                <X className="h-3.5 w-3.5" />
                Cancel
              </Button>
            </div>
          )}

          {(phase === "listening" || phase === "paused") && (
            <div className="flex flex-col items-center justify-center gap-4 py-2 text-center">
              <div className="relative h-36 w-36 shrink-0">
                {phase === "paused" ? (
                  <div className="flex h-full w-full items-center justify-center">
                    <div className="relative flex h-[4.5rem] w-[4.5rem] items-center justify-center rounded-full bg-gradient-to-br from-amber-400 to-amber-600 text-white shadow-lg shadow-amber-500/35 ring-4 ring-amber-400/25">
                      <Pause className="h-7 w-7" />
                    </div>
                  </div>
                ) : (
                  <VoicePoweredOrb
                    className="h-full w-full"
                    hue={350}
                    enableVoiceControl={false}
                    externalAudioLevel={audioLevel}
                  />
                )}
              </div>
              <div>
                <p
                  className={cn(
                    "text-xs font-semibold uppercase tracking-widest",
                    phase === "listening" ? "text-rose-700" : "text-amber-800 dark:text-amber-300",
                  )}
                >
                  {phase === "listening" ? "Listening" : "Paused"}
                </p>
                <p className="mt-1 text-3xl font-semibold tabular-nums">{formatElapsed(elapsed)}</p>
                {phase === "listening" && speechStatus === "reconnecting" && (
                  <p className="mt-1 flex items-center justify-center gap-1.5 text-xs font-medium text-amber-700 dark:text-amber-300">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Connection lost - reconnecting. Still recording; this part will be transcribed once it's back.
                  </p>
                )}
                {phase === "listening" && speechStatus !== "reconnecting" && (
                  <p className="mt-1 text-[10px] text-muted-foreground">
                    {connectionState === "connecting" && !speechReady
                      ? "Starting…"
                      : connectionState === "error"
                        ? "Connection issue - you can still finish and review"
                        : sttMode === "azure" && speechReady
                          ? "Secure transcription with speaker labels - pauses are fine"
                          : "Listening - pauses in conversation are fine"}
                  </p>
                )}
                {phase === "listening" && !voiceDetected && !transcript && elapsed >= 8 && (
                  <p className="mt-1 text-xs font-medium text-amber-700">
                    Haven't heard anything yet - check the right microphone is selected in your
                    browser.
                  </p>
                )}
              </div>
              {safetyBanner}
              <div className="flex flex-wrap justify-center gap-2">
                {phase === "listening" ? (
                  <Button variant="outline" className="h-10 gap-2" onClick={pause}>
                    <Pause className="h-4 w-4" />
                    Pause
                  </Button>
                ) : (
                  <Button variant="outline" className="h-10 gap-2" onClick={resume}>
                    <Play className="h-4 w-4" />
                    Resume
                  </Button>
                )}
                <Button
                  className="h-10 gap-2 bg-rose-600 hover:bg-rose-700"
                  onClick={() => void finishProcess()}
                >
                  <Square className="h-3.5 w-3.5 fill-current" />
                  Finish
                </Button>
                <Button variant="ghost" className="h-10 gap-2" onClick={cancelRestart}>
                  <RotateCcw className="h-4 w-4" />
                  Restart
                </Button>
              </div>
            </div>
          )}

          {phase === "processing" && (
            <div className="flex flex-col items-center justify-center gap-5 py-8">
              <Loader2 className="h-10 w-10 animate-spin text-primary" />
              <div className="w-full max-w-sm text-center">
                <p className="text-sm font-bold">Building your draft note</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {PROCESS_STEPS[Math.min(processStep, PROCESS_STEPS.length - 1)]?.detail}
                </p>
                <p className="mt-0.5 text-[10px] text-muted-foreground/80">
                  Usually under a minute
                </p>
                <div className="mt-4 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full rounded-full bg-primary transition-all duration-700 ease-out"
                    style={{ width: `${processProgress(processStep)}%` }}
                  />
                </div>
              </div>
              <ol className="w-full max-w-sm space-y-2">
                {PROCESS_STEPS.map((step, i) => {
                  const done = processStep > i;
                  const current = processStep === i;
                  return (
                    <li
                      key={step.label}
                      className={cn(
                        "flex items-center gap-2.5 rounded-xl border px-3 py-2 text-xs font-medium transition-colors",
                        done && "border-emerald-200 bg-emerald-50 text-emerald-800 dark:bg-emerald-500/10 dark:text-emerald-300 dark:border-emerald-500/30",
                        current && "border-primary/30 bg-primary/5 text-foreground",
                        !done && !current && "border-border text-muted-foreground",
                      )}
                    >
                      {done ? (
                        <Check className="h-3.5 w-3.5 shrink-0" />
                      ) : current ? (
                        <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
                      ) : (
                        <span className="h-3.5 w-3.5 shrink-0 rounded-full border border-current opacity-40" />
                      )}
                      {step.label}
                    </li>
                  );
                })}
              </ol>
            </div>
          )}

          {phase === "dictate" && (
            <div className="flex flex-1 flex-col gap-3">
              {(errorMessage || dictateError) && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-500/10 dark:text-amber-200 dark:border-amber-500/30">
                  {dictateError || errorMessage}
                </div>
              )}
              <div>
                <p className="text-sm font-bold">Dictate or paste</p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Type, paste, or dictate by voice, then generate a structured clinical note.
                </p>
              </div>
              <Textarea
                value={dictateText}
                onChange={(e) => setDictateText(e.target.value)}
                placeholder="e.g. 52-year-old with 3-day productive cough, low-grade fever…"
                className="min-h-[140px] flex-1 resize-y text-sm"
              />
              <div className="flex flex-wrap gap-2">
                <VoiceDictateButton
                  onText={(t) => setDictateText((prev) => (prev ? `${prev} ${t}` : t))}
                  onError={setDictateError}
                />
                <Button
                  size="sm"
                  className="gap-1.5"
                  disabled={!dictateText.trim()}
                  onClick={() => void generateFromDictate()}
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  Generate note
                </Button>
                <Button size="sm" variant="ghost" onClick={listenAgain}>
                  Back
                </Button>
              </div>
            </div>
          )}

          {phase === "preview" && (
            <div className="flex min-h-0 flex-1 flex-col">
              <div className="grid min-h-0 flex-1 gap-4 overflow-y-auto lg:grid-cols-2">
                {/* Left: transcript + AI suggestions */}
                <div className="space-y-3">
                  {safetyBanner}
                  <div className="rounded-xl border border-border bg-muted/30 p-3">
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <p className="text-xs font-semibold text-muted-foreground">
                        Visit transcript
                      </p>
                      <button
                        type="button"
                        className="flex items-center gap-1 text-[10px] font-bold text-primary disabled:opacity-50"
                        disabled={labelLoading}
                        onClick={() => {
                          if (labeledTranscript) {
                            setShowSpeakers((v) => !v);
                          } else {
                            setShowSpeakers(true);
                            void labelSpeakers();
                          }
                        }}
                      >
                        {labelLoading ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Users className="h-3 w-3" />
                        )}
                        {labeledTranscript && showSpeakers ? "Raw" : "Speakers"}
                      </button>
                    </div>
                    <p className="max-h-48 overflow-y-auto whitespace-pre-wrap text-xs leading-relaxed text-foreground/90">
                      {showSpeakers && labeledTranscript ? labeledTranscript : transcript}
                    </p>
                  </div>

                  {uncertainties.length > 0 && (
                    <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 dark:bg-amber-500/10 dark:text-amber-200 dark:border-amber-500/30">
                      <p className="mb-1 font-bold">Review suggested</p>
                      <ul className="list-disc space-y-1 pl-4">
                        {uncertainties.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {cdsAlerts.length > 0 && (
                    <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-xs text-rose-900 dark:bg-rose-500/10 dark:text-rose-200 dark:border-rose-500/30">
                      <p className="mb-1 font-bold">Clinical alerts</p>
                      <ul className="space-y-2">
                        {cdsAlerts.map((alert) => (
                          <li key={alert.id}>
                            <span className="font-semibold">{alert.title}: </span>
                            {alert.description}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {insightsLoading && (
                    <div className="flex items-center gap-2 rounded-xl border border-border bg-muted/20 p-3 text-xs text-muted-foreground">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Checking billing and orders…
                    </div>
                  )}
                  {billing && (
                    <div className="rounded-xl border border-sky-200 bg-sky-50/60 p-3 text-xs dark:bg-sky-500/10 dark:border-sky-500/30">
                      <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-sky-800 dark:text-sky-300">
                        <Receipt className="h-3.5 w-3.5" />
                        Billing suggestions
                      </p>
                      {billing.em_code && (
                        <p className="text-foreground/90">
                          <Badge className="mr-1.5 bg-sky-600 text-[10px] text-white">
                            E/M {billing.em_code}
                          </Badge>
                          {billing.em_rationale}
                        </p>
                      )}
                      {(billing.cpt_suggestions?.length ?? 0) > 0 && (
                        <ul className="mt-2 space-y-1 text-foreground/90">
                          {billing.cpt_suggestions!.map((p) => (
                            <li key={p.code}>
                              <Badge variant="outline" className="mr-1.5 border-sky-400 text-[10px]">
                                {p.code}
                              </Badge>
                              {p.display}
                              {p.rationale && <span className="block pl-1 text-muted-foreground">{p.rationale}</span>}
                            </li>
                          ))}
                        </ul>
                      )}
                      {billing.diagnoses.length > 0 && (
                        <ul className="mt-2 space-y-1 text-foreground/90">
                          {billing.diagnoses.map((dx) => (
                            <li key={dx.code}>
                              <span className="font-semibold">{dx.code}</span> - {dx.display}
                            </li>
                          ))}
                        </ul>
                      )}
                      {(billing.warnings?.length ?? 0) > 0 && (
                        <ul className="mt-2 list-disc space-y-0.5 pl-4 text-[11px] text-amber-800 dark:text-amber-300">
                          {billing.warnings!.map((w) => (
                            <li key={w}>{w}</li>
                          ))}
                        </ul>
                      )}
                      <p className="mt-2 text-[10px] text-muted-foreground">
                        Suggestions only — confirm every code against the documentation before billing.
                      </p>
                    </div>
                  )}
                  {orders &&
                    Boolean(
                      orders.labs?.length ||
                        orders.imaging?.length ||
                        orders.referrals?.length ||
                        orders.medications?.length ||
                        orders.follow_up,
                    ) && (
                      <div className="rounded-xl border border-violet-200 bg-violet-50/60 p-3 text-xs dark:bg-violet-500/10 dark:border-violet-500/30">
                        <p className="mb-1.5 text-xs font-semibold text-violet-800 dark:text-violet-300">
                          Orders & prescriptions discussed
                        </p>
                        <div className="space-y-1.5 text-foreground/90">
                          {orders.labs?.length ? (
                            <p>
                              <span className="font-semibold">Labs:</span> {orders.labs.join(", ")}
                            </p>
                          ) : null}
                          {orders.imaging?.length ? (
                            <p>
                              <span className="font-semibold">Imaging:</span> {orders.imaging.join(", ")}
                            </p>
                          ) : null}
                          {orders.medications?.map((med, i) => (
                            <div key={`${med.name}-${i}`}>
                              {med.name}
                              {med.dose ? ` ${med.dose}` : ""}
                              {med.frequency ? ` ${med.frequency}` : ""}
                              {med.warnings?.map((w) => (
                                <p key={w} className="mt-0.5 flex items-start gap-1 text-amber-800 dark:text-amber-300">
                                  <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                                  {w}
                                </p>
                              ))}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  {avsSummary && (
                    <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-3 text-xs dark:bg-emerald-500/10 dark:border-emerald-500/30">
                      <p className="mb-1.5 text-xs font-semibold text-emerald-800 dark:text-emerald-300">
                        Patient summary
                      </p>
                      <p className="whitespace-pre-wrap leading-relaxed text-foreground/90">{avsSummary}</p>
                    </div>
                  )}
                </div>

                {/* Right: editable note sections */}
                <div className="space-y-3">
                  <p className="text-xs font-semibold text-muted-foreground">
                    Edit note sections
                  </p>
                  {Object.entries(sections).map(([key, body]) => (
                    <div key={key} className="rounded-xl border border-border bg-card p-3">
                      <label
                        htmlFor={`scribe-section-${key}`}
                        className="mb-2 block text-xs font-semibold text-primary"
                      >
                        {key.replace(/_/g, " ")}
                      </label>
                      <Textarea
                        id={`scribe-section-${key}`}
                        value={body ?? ""}
                        onChange={(e) => updateSection(key, e.target.value)}
                        className="min-h-[100px] resize-y text-sm leading-relaxed"
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-4 flex shrink-0 flex-wrap gap-2 border-t border-border bg-card/80 pt-4">
                <Button className="gap-1.5" onClick={applyToNote}>
                  <Check className="h-4 w-4" />
                  Apply to note
                </Button>
                <Button
                  variant="outline"
                  className="gap-1.5"
                  disabled={avsLoading}
                  onClick={() => void generateAvsSummary()}
                >
                  {avsLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <FileText className="h-4 w-4" />
                  )}
                  Patient summary
                </Button>
                <Button variant="outline" className="gap-1.5" onClick={listenAgain}>
                  <RotateCcw className="h-4 w-4" />
                  Listen again
                </Button>
                <Button variant="ghost" className="gap-1.5" onClick={openDictate}>
                  Dictate
                </Button>
              </div>
            </div>
          )}
        </div>

        {showDictateSide && (
          <div className="flex flex-col overflow-hidden bg-gradient-to-b from-muted/30 to-muted/10 p-5">
            <p className="mb-3 text-xs font-semibold text-muted-foreground">
              Live dictation
            </p>
            <div className="flex-1 overflow-y-auto whitespace-pre-wrap rounded-xl border border-border/70 bg-card/80 p-3 text-xs text-muted-foreground shadow-inner backdrop-blur-sm">
              {dictateText.trim() || "Your dictation will appear here as you type or speak."}
            </div>
          </div>
        )}
      </div>
        </div>
      </div>
    </div>
    </>
  );

  return createPortal(panel, document.body);
}
